import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from 'recharts';
import { formatDuration } from 'src/services/coCustomerAnalyticsService';
import { useAppSelector } from 'src/store';

const MOMENT_DURATION_MAP = {
  Sec: 'seconds',
  Min: 'minutes',
  Hours: 'hours',
};

export const AnalyticsCustomerLineChartData = ({
  title = '',
  dataKey = '',
  data = [],
  groupBy = 'day',
  color = null,
}) => {
  const [dataKeyY, setDataKeyY] = useState(dataKey);
  const [labelY, setLabelY] = useState('');
  const { dateY } = useAppSelector((s) => s.analyticSlices);

  const isDayRange = groupBy === 'day';
  const isTimeFormat = ['firstResponseTimeAvg', 'resolutionTimeAvg', 'responseTimeAvg'].includes(
    dataKey,
  );

  const tooltipFormatter = (value) => {
    if (isTimeFormat) {
      return value ? formatDuration(value, MOMENT_DURATION_MAP[labelY]) : 0;
    }

    return `${value} ${labelY}`;
  };

  useEffect(() => {
    let YAxisLabel = isTimeFormat ? 'Min' : 'Tickets';
    YAxisLabel = dataKey === 'resolutionRate' ? '%' : YAxisLabel;
    YAxisLabel = ['aiRepliesPerProcessedTicket', 'aiRepliesSent'].includes(dataKey)
      ? 'Replies'
      : YAxisLabel;
    if (dateY && YAxisLabel !== '%') {
      if (dateY === 'sec') {
        setDataKeyY(`${dataKey}_sec`);
        YAxisLabel = 'Sec';
      } else if (dateY === 'min') {
        setDataKeyY(`${dataKey}_min`);
        YAxisLabel = 'Min';
      } else if (dateY === 'hour') {
        setDataKeyY(`${dataKey}_hr`);
        YAxisLabel = 'Hours';
      }
    }
    setLabelY(YAxisLabel);
  }, [dateY, dataKey]);

  return (
    <ResponsiveContainer width="100%" height="100%" minHeight={'500px'}>
      <LineChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 50,
          right: 70,
          left: 10,
          bottom: 15,
        }}
      >
        <CartesianGrid vertical={false} strokeDasharray="1 1" />
        <XAxis
          tickFormatter={(value) => {
            if (typeof value === 'string' && value.includes(':')) {
              return value.replace(':00', '').replace(/^0/, '');
            }
            return value;
          }}
          tick={{
            fontSize: 11,
            fontFamily: 'Bai Jamjuree',
            fontWeight: 500,
            opacity: 0.3,
            dy: 7,
          }}
          tickLine={false}
          axisLine={{ stroke: '#DFE3E8' }}
          dataKey="formattedGroup"
        >
          <Label
            value={isDayRange ? 'Days' : 'Hours'}
            position="right"
            dy={-15}
            dx={10}
            className={'fill-ebony text-sm font-semibold'}
          />
        </XAxis>
        <YAxis
          tick={{
            fontSize: 11,
            fontFamily: 'Bai Jamjuree',
            fontWeight: 500,
            opacity: 0.3,
            dx: -7,
          }}
          tickLine={false}
          axisLine={{ stroke: '#DFE3E8' }}
        >
          <Label
            value={labelY}
            dy={-15}
            dx={25}
            position="top"
            className={'fill-ebony text-sm font-semibold'}
          />
        </YAxis>
        <Tooltip
          formatter={tooltipFormatter}
          labelStyle={{
            color: '#05050D',
            fontFamily: 'Bai Jamjuree',
            fontSize: '12px',
            opacity: 0.3,
          }}
          contentStyle={{
            borderRadius: '5px',
            background: '#FFF',
          }}
          itemStyle={{
            fontFamily: 'Bai Jamjuree',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 600,
          }}
        />
        {Boolean(data.length) && (
          <ReferenceLine
            x={data[parseInt((data.length / 2).toFixed(0)) - 1]['formattedGroup']}
            stroke="#CCCCCC"
            strokeDasharray="1 1"
          />
        )}
        <Line
          dataKey={dataKeyY}
          name={title}
          stroke={color || '#5D4EC7'}
          strokeWidth={3}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
